import React from 'react';
import { useRoute } from '@react-navigation/native';

import ColorWrapper from '@styles/colors';

import { scale as initialScale } from '@components/frame';
import { font as initialFont } from '@styles/fonts';

import { scheduledProductionOperationIdParam } from '@components/operatorPanel/constants/operatorPanel.constants';
import { useBuildLinkListByPerspective } from '@components/operatorPanel/hooks/useBuildlLinkListByPerspective';
import AttachEntitiesList from '@components/common/AttachEntitiesList';
import { AttachEntitiesListProps, OnAttachItemPress, OnAttachItemPressLinkParam } from '@components/common/AttachEntitiesList/types';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { handleRemoveAttachment } from '@reducers/v2/imageHandlingV2/thunks/handleRemoveAttachment';
import {
  ImageDocumentStoreItem,
  ODataOrRetrievedDocumentLink,
  RetrievedDocumentReferenceLink,
} from '../../../v2/components/imageHandling/types/types';
import { updateImageMetadata } from '@reducers/v2/imageHandlingV2/thunks';

interface AttachEntitiesListOPProps extends Pick<AttachEntitiesListProps, 'C' | 'font' | 'scale' | 'imageDocumentLinks' | 'style'> {
  imageDocumentLinks: ODataOrRetrievedDocumentLink[];
  selectedImageDocument: ImageDocumentStoreItem | null;
  onChange?: (link?: OnAttachItemPressLinkParam) => void;
}

const AttachEntitiesListOP: React.FC<AttachEntitiesListOPProps> = ({
  C,
  imageDocumentLinks,
  selectedImageDocument,
  style,
  scale = initialScale,
  font = initialFont,
  onChange,
}) => {
  const dispatch = useAppDispatch();
  const route = useRoute();
  const selectedJobId = route.params?.[scheduledProductionOperationIdParam];

  const imagesDocumentsReferences = useBuildLinkListByPerspective(selectedJobId);

  const documentId = selectedImageDocument?.documentId ?? selectedImageDocument?.id;

  const onAttachItemPress: OnAttachItemPress = async ({ link: { isSelected, scopeId, scopeReferenceId, scopeReferenceName }, setLoadingState }) => {
    onChange?.({ scopeId, scopeReferenceId, scopeReferenceName, isSelected });

    if (!selectedImageDocument) {
      return;
    }

    setLoadingState({ id: scopeReferenceId, loading: true });

    if (isSelected) {
      const link = imageDocumentLinks.find((link) => link.scopeReferenceId === scopeReferenceId && link.scopeId === scopeId);

      if (link) {
        await dispatch(
          handleRemoveAttachment({
            img: { ...selectedImageDocument, id: documentId },
            attachmentToRemove: link as RetrievedDocumentReferenceLink,
          })
        );
      }
    } else {
      const backendData = {
        documentId,
        documentLinks: [
          {
            scopeId,
            scopeReferenceId,
            scopeReferenceName,
            isFavorite: false,
            isActivated: true,
          },
        ],
      };

      await dispatch(updateImageMetadata({ data: backendData }));
    }

    setLoadingState({ id: null, loading: false });
  };

  return (
    <AttachEntitiesList
      imageDocumentLinks={imageDocumentLinks}
      imageDocumentReferences={imagesDocumentsReferences}
      onAttachItemPress={onAttachItemPress}
      scale={scale}
      font={font}
      style={style}
    />
  );
};

export default ColorWrapper(AttachEntitiesListOP);
